/**
 * MusicQuoteRequestFlow - Quote request flow for repairs, custom builds, and complex services
 * Flow: Service → Details → Media → Contact → Review
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Wrench, ChevronRight, ChevronLeft, CheckCircle, FileText,
  User, Mail, Phone, Upload, Image, Loader2, Clock, MapPin,
  AlertCircle, Camera
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quote Types
const QUOTE_TYPES = {
  repair: {
    name: 'Instrument Repair',
    icon: '🔧',
    description: 'Get quotes for fixing your instrument',
    fields: ['instrument_type', 'brand', 'issue_description', 'urgency'],
  },
  setup: {
    name: 'Professional Setup',
    icon: '⚙️',
    description: 'Guitar, bass, or other instrument setup',
    fields: ['instrument_type', 'brand', 'setup_type'],
  },
  custom_build: {
    name: 'Custom Build / Modification',
    icon: '🎸',
    description: 'Custom instruments or modifications',
    fields: ['instrument_type', 'modification_description', 'budget_range'],
  },
  restoration: {
    name: 'Restoration',
    icon: '🪄',
    description: 'Vintage instrument restoration',
    fields: ['instrument_type', 'brand', 'year', 'condition_description'],
  },
  moving: {
    name: 'Instrument Moving',
    icon: '🚚',
    description: 'Piano moving, drum kit transport',
    fields: ['instrument_type', 'from_location', 'to_location', 'floor_access'],
  },
};

// Urgency Options
const URGENCY_OPTIONS = [
  { id: 'standard', name: 'Standard', description: '1-2 weeks', icon: '📅' },
  { id: 'priority', name: 'Priority', description: '3-5 days', icon: '⚡' },
  { id: 'emergency', name: 'Emergency', description: 'ASAP', icon: '🚨' },
];

// Instrument Types
const INSTRUMENT_TYPES = [
  'Electric Guitar', 'Acoustic Guitar', 'Bass Guitar', 'Classical Guitar',
  'Piano', 'Digital Piano', 'Keyboard', 'Synthesizer',
  'Drums', 'Electronic Drums', 'Percussion',
  'Violin', 'Cello', 'Viola', 'Double Bass',
  'Saxophone', 'Trumpet', 'Flute', 'Clarinet',
  'Amplifier', 'Effects Pedal', 'DJ Equipment',
  'Other'
];

// Budget Ranges
const BUDGET_RANGES = {
  UK: ['Under £100', '£100-250', '£250-500', '£500-1000', '£1000+'],
  LK: ['Under Rs.15,000', 'Rs.15,000-30,000', 'Rs.30,000-75,000', 'Rs.75,000-150,000', 'Rs.150,000+'],
};

// Steps
const STEPS = [
  { id: 1, name: 'Service', icon: Wrench },
  { id: 2, name: 'Details', icon: FileText },
  { id: 3, name: 'Photos', icon: Camera },
  { id: 4, name: 'Contact', icon: Phone },
  { id: 5, name: 'Review', icon: CheckCircle },
];

// Step Progress Component
function StepProgress({ currentStep }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isComplete = currentStep > step.id;
          
          return (
            <React.Fragment key={step.id}>
              <div className={`flex flex-col items-center ${isActive ? 'text-orange-600' : isComplete ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isActive ? 'bg-orange-100 border-2 border-orange-600' :
                  isComplete ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className="text-xs mt-1 font-medium hidden sm:block">{step.name}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${isComplete ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Main Component
export default function MusicQuoteRequestFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country } = useCountry();
  
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [quoteType, setQuoteType] = useState(searchParams.get('type') || '');
  const [instrumentType, setInstrumentType] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [urgency, setUrgency] = useState('standard');
  const [budgetRange, setBudgetRange] = useState('');
  const [photos, setPhotos] = useState([]);
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [floorAccess, setFloorAccess] = useState('');
  
  // Contact
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [city, setCity] = useState('');
  const [notes, setNotes] = useState('');
  
  const currencySymbol = country === 'LK' || country === 'SL' ? 'Rs.' : '£';
  const budgets = BUDGET_RANGES[country === 'LK' ? 'LK' : 'UK'];
  
  // Handle photo upload (mock)
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photos.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }
    
    // Create preview URLs
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setPhotos([...photos, ...newPhotos]);
  };
  
  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };
  
  // Handle submission
  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/music/quote-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quote_type: quoteType,
          instrument_type: instrumentType,
          brand: brand || null,
          model: model || null,
          year: year || null,
          issue_description: issueDescription,
          urgency: urgency,
          budget_range: budgetRange || null,
          from_location: fromLocation || null,
          to_location: toLocation || null,
          floor_access: floorAccess || null,
          photos_count: photos.length,
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: customerPhone || null,
          city: city,
          notes: notes || null,
          country: country,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Quote request submitted!');
        setStep(6); // Success step
      } else {
        toast.error(data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit quote request');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Success Step
  if (step === 6) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Quote Request Submitted!</h2>
            <p className="text-gray-500 mb-4">
              We'll notify matching providers in your area. Expect quotes within 24-48 hours.
            </p>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 mb-6">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              You'll receive quotes via email. Compare and choose the best offer.
            </div>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/')}>
                Home
              </Button>
              <Button onClick={() => navigate('/music')}>
                Browse More
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6" />
            Request a Quote
          </h1>
          <p className="text-orange-100">Get quotes from verified local providers</p>
        </div>
      </div>
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        <StepProgress currentStep={step} />
        
        {/* Step 1: Quote Type Selection */}
        {step === 1 && (
          <Card data-testid="quote-step-1">
            <CardHeader>
              <CardTitle>What do you need?</CardTitle>
              <CardDescription>Select the type of service you're looking for</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup value={quoteType} onValueChange={setQuoteType}>
                {Object.entries(QUOTE_TYPES).map(([key, type]) => (
                  <label 
                    key={key}
                    data-testid={`quote-type-${key}`}
                    className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      quoteType === key ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <RadioGroupItem value={key} />
                    <span className="text-2xl">{type.icon}</span>
                    <div>
                      <p className="font-medium">{type.name}</p>
                      <p className="text-sm text-gray-500">{type.description}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
              
              <Button 
                data-testid="quote-continue-btn"
                className="w-full bg-gradient-to-r from-orange-500 to-amber-600 mt-4"
                onClick={() => setStep(2)}
                disabled={!quoteType}
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Step 2: Details */}
        {step === 2 && (
          <Card data-testid="quote-step-2">
            <CardHeader>
              <CardTitle>Tell us more</CardTitle>
              <CardDescription>Provide details about your {QUOTE_TYPES[quoteType]?.name.toLowerCase()}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Instrument Type *</Label>
                <Select value={instrumentType} onValueChange={setInstrumentType}>
                  <SelectTrigger className="mt-2" data-testid="instrument-select">
                    <SelectValue placeholder="Select instrument" />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTRUMENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Brand</Label>
                  <Input 
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    placeholder="e.g. Fender, Gibson"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Model</Label>
                  <Input 
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    placeholder="e.g. Stratocaster"
                    className="mt-2"
                  />
                </div>
              </div>
              
              {(quoteType === 'restoration' || quoteType === 'repair') && (
                <div>
                  <Label>Year (if known)</Label>
                  <Input 
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    placeholder="e.g. 1965"
                    className="mt-2"
                  />
                </div>
              )}
              
              <div>
                <Label>Describe the issue/requirement *</Label>
                <Textarea 
                  value={issueDescription}
                  onChange={(e) => setIssueDescription(e.target.value)}
                  placeholder={
                    quoteType === 'repair' ? "What's wrong with your instrument? Describe the issue in detail..." :
                    quoteType === 'custom_build' ? "Describe what you want built or modified..." :
                    quoteType === 'restoration' ? "Describe the current condition and what restoration you need..." :
                    quoteType === 'moving' ? "Describe what needs to be moved and any special requirements..." :
                    "Describe what you need..."
                  }
                  className="mt-2"
                  rows={4}
                  data-testid="issue-description"
                />
              </div>
              
              {(quoteType === 'repair' || quoteType === 'setup') && (
                <div>
                  <Label>Urgency</Label>
                  <RadioGroup value={urgency} onValueChange={setUrgency} className="mt-2 grid grid-cols-3 gap-2">
                    {URGENCY_OPTIONS.map(option => (
                      <label 
                        key={option.id}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          urgency === option.id ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <RadioGroupItem value={option.id} className="sr-only" />
                        <span className="text-xl mb-1">{option.icon}</span>
                        <span className="text-sm font-medium">{option.name}</span>
                        <span className="text-xs text-gray-500">{option.description}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              )}
              
              {quoteType === 'moving' && (
                <>
                  <div>
                    <Label>From Location *</Label>
                    <Input 
                      value={fromLocation}
                      onChange={(e) => setFromLocation(e.target.value)}
                      placeholder="Pickup address"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>To Location *</Label>
                    <Input 
                      value={toLocation}
                      onChange={(e) => setToLocation(e.target.value)}
                      placeholder="Delivery address"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Floor Access</Label>
                    <Select value={floorAccess} onValueChange={setFloorAccess}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select floor access" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ground">Ground Floor</SelectItem>
                        <SelectItem value="lift">Has Lift/Elevator</SelectItem>
                        <SelectItem value="stairs_1">1-2 Floors (Stairs)</SelectItem>
                        <SelectItem value="stairs_3">3+ Floors (Stairs)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {(quoteType === 'custom_build' || quoteType === 'restoration') && (
                <div>
                  <Label>Budget Range</Label>
                  <Select value={budgetRange} onValueChange={setBudgetRange}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select your budget" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgets.map(budget => (
                        <SelectItem key={budget} value={budget}>{budget}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600"
                  onClick={() => setStep(3)}
                  disabled={!instrumentType || !issueDescription}
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 3: Photos */}
        {step === 3 && (
          <Card data-testid="quote-step-3">
            <CardHeader>
              <CardTitle>Add Photos (Optional)</CardTitle>
              <CardDescription>Photos help providers give more accurate quotes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 font-medium">Click to upload photos</p>
                  <p className="text-sm text-gray-400 mt-1">PNG, JPG up to 5MB each (max 5 photos)</p>
                </label>
              </div>
              
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img 
                        src={photo.preview} 
                        alt={`Upload ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-sm"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <Image className="w-4 h-4 inline mr-1" />
                <strong>Tip:</strong> Include photos of any damage, serial numbers, or areas that need work.
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600"
                  onClick={() => setStep(4)}
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 4: Contact */}
        {step === 4 && (
          <Card data-testid="quote-step-4">
            <CardHeader>
              <CardTitle>Your Contact Details</CardTitle>
              <CardDescription>How can providers reach you?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input 
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="John Smith"
                  className="mt-2"
                  data-testid="customer-name"
                />
              </div>
              
              <div>
                <Label>Email Address *</Label>
                <Input 
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="mt-2"
                  data-testid="customer-email"
                />
              </div>
              
              <div>
                <Label>Phone Number</Label>
                <Input 
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder={country === 'LK' ? '+94 7x xxx xxxx' : '+44 7xxx xxxxxx'}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label>Your City *</Label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger className="mt-2" data-testid="city-select">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {country === 'LK' ? (
                      <>
                        <SelectItem value="Colombo">Colombo</SelectItem>
                        <SelectItem value="Kandy">Kandy</SelectItem>
                        <SelectItem value="Galle">Galle</SelectItem>
                        <SelectItem value="Jaffna">Jaffna</SelectItem>
                        <SelectItem value="Negombo">Negombo</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="London">London</SelectItem>
                        <SelectItem value="Manchester">Manchester</SelectItem>
                        <SelectItem value="Birmingham">Birmingham</SelectItem>
                        <SelectItem value="Leeds">Leeds</SelectItem>
                        <SelectItem value="Bristol">Bristol</SelectItem>
                        <SelectItem value="Liverpool">Liverpool</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Additional Notes</Label>
                <Textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any other information providers should know..."
                  className="mt-2"
                  rows={3}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setStep(3)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600"
                  onClick={() => setStep(5)}
                  disabled={!customerName || !customerEmail || !city}
                >
                  Review Quote
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Step 5: Review */}
        {step === 5 && (
          <Card data-testid="quote-step-5">
            <CardHeader>
              <CardTitle>Review Your Quote Request</CardTitle>
              <CardDescription>Confirm all details are correct</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quote Type */}
              <div className="flex items-center gap-4 p-4 bg-orange-50 rounded-lg">
                <span className="text-3xl">{QUOTE_TYPES[quoteType]?.icon}</span>
                <div>
                  <h4 className="font-semibold">{QUOTE_TYPES[quoteType]?.name}</h4>
                  <p className="text-sm text-gray-500">{instrumentType}</p>
                </div>
                {urgency !== 'standard' && (
                  <Badge className={urgency === 'emergency' ? 'bg-red-500' : 'bg-amber-500'}>
                    {URGENCY_OPTIONS.find(u => u.id === urgency)?.name}
                  </Badge>
                )}
              </div>
              
              {/* Details Summary */}
              <div className="space-y-3">
                {brand && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Brand / Model</span>
                    <span className="font-medium">{brand} {model && `- ${model}`}</span>
                  </div>
                )}
                {year && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Year</span>
                    <span className="font-medium">{year}</span>
                  </div>
                )}
                {budgetRange && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Budget Range</span>
                    <span className="font-medium">{budgetRange}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Location</span>
                  <span className="font-medium">{city}</span>
                </div>
                {photos.length > 0 && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Photos Attached</span>
                    <span className="font-medium">{photos.length}</span>
                  </div>
                )}
              </div>
              
              {/* Description */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h5 className="font-medium mb-2">Description</h5>
                <p className="text-sm text-gray-600">{issueDescription}</p>
              </div>
              
              {/* Moving Details */}
              {quoteType === 'moving' && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium mb-2">Moving Details</h5>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>From:</strong> {fromLocation}</p>
                    <p><strong>To:</strong> {toLocation}</p>
                    {floorAccess && <p><strong>Floor Access:</strong> {floorAccess}</p>}
                  </div>
                </div>
              )}
              
              {/* Contact */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h5 className="font-medium mb-2">Contact Details</h5>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><User className="w-4 h-4 inline mr-2" />{customerName}</p>
                  <p><Mail className="w-4 h-4 inline mr-2" />{customerEmail}</p>
                  {customerPhone && <p><Phone className="w-4 h-4 inline mr-2" />{customerPhone}</p>}
                </div>
              </div>
              
              {/* Info Box */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <Clock className="w-4 h-4 inline mr-1" />
                <strong>What happens next?</strong> Your request will be sent to providers in {city}. 
                Expect to receive quotes via email within 24-48 hours.
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(4)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600"
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="submit-quote-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Submit Quote Request
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
